import { useSyncExternalStore, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from './useUser';

interface LayoutConfig {
  topPadding: number;
  sidebarTopOffset: number;
}

// Default values
const DEFAULT_HEADER_HEIGHT = 80;
const DEFAULT_ADMIN_HEADER_HEIGHT = 130;

// Extra padding buffer for spacing
const PADDING_BUFFER = 40;

// Store for header height
let headerHeight = 0;
let listeners: Array<() => void> = [];
let resizeObserver: ResizeObserver | null = null;
let currentHeaderElement: Element | null = null;

const notifyListeners = () => {
  listeners.forEach((listener) => listener());
};

const updateHeight = (height: number) => {
  if (height !== headerHeight) {
    headerHeight = height;
    notifyListeners();
  }
};

// Observe the header element
const observeHeader = () => {
  const header = document.querySelector('[data-header-container]');

  // If no header found, retry
  if (!header) {
    requestAnimationFrame(observeHeader);
    return;
  }

  // If same element, just update measurement
  if (header === currentHeaderElement) {
    updateHeight(header.getBoundingClientRect().height);
    return;
  }

  // New element - unobserve old and observe new
  if (currentHeaderElement && resizeObserver) {
    resizeObserver.unobserve(currentHeaderElement);
  }

  currentHeaderElement = header;
  resizeObserver?.observe(header);
  updateHeight(header.getBoundingClientRect().height);
};

// Initialize ResizeObserver once
const initializeObserver = () => {
  if (resizeObserver || typeof window === 'undefined') return;

  resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
      updateHeight(height);
    }
  });

  observeHeader();
};

// Force re-observation of header (call on navigation)
const refreshHeaderObservation = () => {
  // Reset current element to force re-observation
  currentHeaderElement = null;
  // Small delay to let the DOM update after navigation
  requestAnimationFrame(() => {
    requestAnimationFrame(observeHeader);
  });
};

// External store subscription
const subscribe = (listener: () => void) => {
  initializeObserver();
  listeners.push(listener);
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
};

const getSnapshot = () => headerHeight;
const getServerSnapshot = () => 0;

export const useLayoutConfig = (): LayoutConfig => {
  const { user } = useUser();
  const location = useLocation();
  const measuredHeight = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const role = user?.role;
  const isSuperAdmin = role === 'super_admin';

  // Re-observe header when location changes (navigation)
  useEffect(() => {
    refreshHeaderObservation();
  }, [location.pathname]);

  // Use measured height or fallback to defaults
  const defaultHeight = isSuperAdmin ? DEFAULT_ADMIN_HEADER_HEIGHT : DEFAULT_HEADER_HEIGHT;
  const actualHeight = measuredHeight > 0 ? measuredHeight : defaultHeight;

  return {
    topPadding: actualHeight + PADDING_BUFFER,
    sidebarTopOffset: actualHeight,
  };
};
